/**
 * @author: subhasish.ghosh@zoom.us
 */

import GridOnRoundedIcon from "@mui/icons-material/GridOnRounded";
import PieChartOutlineRoundedIcon from "@mui/icons-material/PieChartOutlineRounded";
import {
  Box,
  ButtonBase,
  CircularProgress,
  Divider,
  IconButton,
  Toolbar,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { Switch } from "@zoom/react-ui";
import { AgCharts } from "ag-charts-react";
import { useSnackbar } from "notistack";
import { columnsDashboard, hColumnsDashboard } from "@options/dataGrid";
import React, { useEffect, useMemo, useState } from "react";
import { Chart } from "react-google-charts";
import { useQuery } from "@tanstack/react-query";
import "react-resizable/css/styles.css";
import axiosInstance from "@utils/axiosInstance";
import {
  DEFAULT_SORT_ORDER,
  MONTHS,
  SIDEBAR_ITEMS,
  TABLE_TYPES_KEYS,
  DEFAULT_STATUS_FILTERS,
  NON_ACTIONABLE_PROCESSING_STATUSES
} from "@utils/constants";
import {
  getDataGridSortOrderForPayload,
  getFormattedAmount,
  isCurrentRoleReadonly,
  isSelectedActionTypeBizOperationDirect,
  isSelectedActionTypeTNS,
  isSelectedActionTypeNonPaymentDirect,
  getTop10AndRemoveUncategorisedIndustry,
  isSelectedActionTypeAdmin,
  goToTop,
  applyPromisedToPayFunc,
  isSelectedTableTabPending,
  isSelectedTableTabHistorical,
  getToday,
  getOneYearAgo,
  formatDateString,
  enableWorkflowActionEndpoints,
  isApproveEligibleForSelection,
  isUserEligibleToApproveRow,
  isUserEligibleToDeferRow,
} from "@utils/utils";
import ApprovalRejectionModal from "@components/ApprovalRejectionModal";
import Appbar from "@components/NavBar";
import "@components/styles.css";
import TopBar from "@components/TopBar";
import AdminPortal from "@pages/AdminPortal";
import UserInfoModal from "@components/UserInfoModal";
import classNames from "classnames";
import geoChartOptions from "@options/GoogleCharts/geoChart";
import { sankeyColumns } from "@options/GoogleCharts/sankey";
import unionBy from "lodash/unionBy";
import { useLocalStorage } from "usehooks-ts";
import CancellationTableTab from "@components/CancellationTableTab";
import { HistoricalSearchBar, PendingSearchBar } from "@components/SearchBar";
import { DATE_FORMAT } from "@utils/timeUnits";
import NoRecords from "@components/NoRecords";
import CopiedData from "@components/CopiedData";
import CustomDataGridToolBar from "@components/CustomDataGridToolBar";
import PendingCancellationsHeader from "@components/PendingCancellationsHeader";
import IndustryDataSection from "@components/IndustryDataSection";

const Home = ({ userActionData, userInfoLoading }) => {
  const [isUserInfoModalOpen, setUserInfoModalOpen] = useState(false);

  return (
    <>
      <Box sx={{ display: "flex" }}>
        <Appbar
          appName="Cancellation Dashboard"
          userName={userActionData?.data?.data?.name}
          isUserInfoLoading={userInfoLoading}
          setUserInfoModalOpen={setUserInfoModalOpen}
        />
        <Box component="main" sx={{ flexGrow: 1, padding: "0" }}>
          <Toolbar />
          <CancellationDashboard
            userActionData={userActionData}
            userActionDataLoading={userInfoLoading}
          />
        </Box>
      </Box>
      <UserInfoModal
        isOpen={isUserInfoModalOpen}
        data={userActionData?.data?.data || {}}
        onClose={() => setUserInfoModalOpen(false)}
      />
    </>
  );
};

const CancellationDashboard = ({ userActionData, userActionDataLoading }) => {
  const { enqueueSnackbar } = useSnackbar();
  // const [isPageRefreshed, setIsPageRefreshed] = usePageRefresh();

  const currentRole = useMemo(
    () => userActionData?.data?.data?.role,
    [userActionData]
  );
  const currentRoles = useMemo(
    () => userActionData?.data?.data?.roles,
    [userActionData]
  );
  const currentUserEmail = useMemo(
    () => userActionData?.data?.data?.email,
    [userActionData]
  );
  const isRenewalsDeferApprover = useMemo(
    () => userActionData?.data?.data?.renewalsDeferApprover,
    [userActionData]
  );
  const [isFetchEnabled, setFetchEnabled] = useState(false);
  const [selectedRowIds, setSelectedRowIds] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  // Whether the current user is an assigned approver for the currently-pending
  // level on every selected row. Gates the "Approve Cancellation(s)" button so
  // e.g. a Renewals L1 Approver + Renewals Defer Approver, who can now see
  // L2-pending records thanks to defer-visibility, can't also click Approve
  // on a level they aren't actually the approver for.
  const isApproveEligible = useMemo(
    () => isApproveEligibleForSelection(selectedRows, currentUserEmail),
    [selectedRows, currentUserEmail]
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [historicalSearchTerm, setHistoricalSearchTerm] = useState("");
  const [isApprovalRejectionModalOpen, setApprovalRejectionModalOpen] =
    useState(false);
  const [isApproveAction, setApproveAction] = useState(false);
  const [isExecutionDecline, setExecutionDecline] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [showUserApproved, setShowUserApproved] = useState(false);
  const [statusFilters, setStatusFilters] = useState(DEFAULT_STATUS_FILTERS);
  const [dateRangeFilter, setDateRangeFilter] = useState([
    getOneYearAgo().format(DATE_FORMAT),
    getToday().format(DATE_FORMAT),
  ]);
  const [hideL1Approved, setHideL1Approved] = useState(false);
  const [approverLevel, setApproverLevel] = useState("level_one");
  const [pagination, setPagination] = useState({
    page: 0,
    pageSize: 10,
  });
  const [hPagination, setHPagination] = useState({
    page: 0,
    pageSize: 10,
  });
  const [rowCount, setRowCount] = useState(0);
  const [hRowCount, setHRowCount] = useState(0);
  const [actionType, setActionType] = useState([""]);
  const [selectedActionType, setSelectedActionType] = useState();
  const [selectedTableTab, setSelectedTableTab] = useState();

  const [sortOrder, setSortOrder] = useState([DEFAULT_SORT_ORDER.pending]);
  const [historicalSortOrder, setHistoricalSortOrder] = useState([
    DEFAULT_SORT_ORDER.historical,
  ]);
  const [changeableHistoricalSortOrder, handleChangeHistoricalSortOrder] =
    useState([]);

  const [tData, setTData] = useState([]);
  const [hTData, setHTData] = useState([]);
  const [hasPendingTData, setHasPendingTData] = useState(false);
  const [hasHistoricalTData, setHasHistoricalTData] = useState(false);

  const [geoData, setGeoData] = useState([["Country", "Cancellations"]]);

  const [showAllIndustryData, setShowAllIndustryData] = useState(false);
  const [industryTableData, setIndustryTableData] = useState([]);
  const [industryPieData, setIndustryPieData] = useState({});
  const [industrySankeyData, setIndustrySankeyData] = useState([sankeyColumns]);

  const [pastSubsCancellationCount, setPastSubsCancellationCount] = useState(
    {}
  );

  const [
    pendingCollectionsFailureDirectIndirect,
    setPendingCollectionsFailureDirectIndirect,
  ] = useState({});

  const [userEmail, setUserEmail] = useState();

  const [pieDataLoading, setPieDataLoading] = useState(true);
  const [daysDataLoading, setDaysDataLoading] = useState(true);
  const [monthDataLoading, setMonthDataLoading] = useState(true);
  const [tDataLoading, setTDataLoading] = useState(true);
  const [hTDataLoading, setHTDataLoading] = useState(true);

  const [copiedData, setCopiedData] = useState("");

  const [showCharts, setShowCharts] = useLocalStorage("show-charts", true);

  const gridColumnState = useMemo(
    () => ({
      columns: {
        columnVisibilityModel: {
          accountNumber: true,
          name: true,
          ticketNumber:
            isSelectedActionTypeBizOperationDirect(selectedActionType),
          dueIn: true,
          zoomAccountNumber: true,
          affectedAccountCount: true,
          industry: true,
          accountOwnerDivision: true,
          ownerName: true,
          accountOwnerManagerName: true,
          subscriptionName: true,
          subscriptionTermEndDate: true,
          segment: false,
          salesChannelDisplayName:
            isSelectedTableTabHistorical(selectedTableTab),
          mrrUSD: true,
          accountBalanceUSD: true,
          currency: true,
          // subOriginalCreatedDate: true,
          businessComment: false,
          level_1_approver: true,
          level_2_approver: true,
          cancellationReason: true,
          reasonCode: true,
          detailedInfo: false,
          status: true,
          processingStatus: true,
          errorMessage: true,
          dwaCreatedDate: true,
          deferCancellation: true,
        },
      },
      // pinnedColumns: { left: ["__check__"] },
      pagination: { paginationModel: { pageSize: 10 } },
      sorting: {
        sortModel: [],
      },
    }),
    [selectedActionType, selectedTableTab]
  );

  const {
    data: geoMapData,
    isFetching: geoMapDataLoading,
    refetch: geoMapDataRefetch,
  } = useQuery({
    queryKey: `subscriptions-cancellation-map-${selectedActionType}`,
    queryFn: async () => {
      setGeoData([["Country", "Cancellations"]]);
      return await axiosInstance.get(
        `/operationmonitor/api/v1/workflow-action/geo-chart`,
        {
          params: {
            actionType: selectedActionType,
          },
        }
      );
    },
    ...{
      enabled:
        isFetchEnabled && enableWorkflowActionEndpoints(selectedActionType),
      refetchOnWindowFocus: false,
    },
  });

  const {
    data: tableData,
    isFetching: isTableDataFetching,
    refetch: tableDataRefetch,
  } = useQuery({
    queryKey: `${selectedActionType}-${userEmail}-${searchTerm}-${showAll}-${hideL1Approved}-${approverLevel}-${JSON.stringify(
      sortOrder
    )}-${JSON.stringify(pagination)}-pending-data`,
    queryFn: async () => {
      setTDataLoading(true);
      setTData([]);
      return await axiosInstance.post(
        `/operationmonitor/api/v1/workflow-action`,
        {
          actionType: selectedActionType,
          searchTerm: searchTerm,
          showAll: showAll,
          ...(showAll && { hideL1Status: hideL1Approved }),
          ...(!showAll &&
            isSelectedActionTypeNonPaymentDirect(selectedActionType) && {
            approverLevel: approverLevel,
          }),
          sort: getDataGridSortOrderForPayload(sortOrder, TABLE_TYPES_KEYS[0]),
          offset: pagination.page + 1,
          limit: pagination.pageSize,
        }
      );
    },
    ...{
      enabled:
        isFetchEnabled &&
        isSelectedTableTabPending(selectedTableTab) &&
        enableWorkflowActionEndpoints(selectedActionType),
      refetchOnWindowFocus: false,
    },
  });

  const { data: tableCount, refetch: tableCountRefetch } = useQuery({
    queryKey: `${selectedActionType}-${userEmail}-${searchTerm}-${showAll}-${hideL1Approved}-${approverLevel}-pending-datacount`,
    queryFn: async () =>
      await axiosInstance.post(
        `/operationmonitor/api/v1/workflow-action/count`,
        {
          actionType: selectedActionType,
          searchTerm: searchTerm,
          showAll: showAll,
          ...(showAll && { hideL1Status: hideL1Approved }),
          ...(!showAll &&
            isSelectedActionTypeNonPaymentDirect(selectedActionType) && {
            approverLevel: approverLevel,
          }),
        }
      ),
    ...{
      enabled:
        isFetchEnabled &&
        isSelectedTableTabPending(selectedTableTab) &&
        enableWorkflowActionEndpoints(selectedActionType),
      refetchOnWindowFocus: false,
    },
  });

  const {
    data: hTableData,
    isFetching: isHTableDataFetching,
    refetch: hTableDataRefetch,
  } = useQuery({
    queryKey: `${selectedActionType}-${userEmail}-${historicalSearchTerm}-${showUserApproved}-${JSON.stringify(
      dateRangeFilter
    )}-${JSON.stringify(historicalSortOrder)}-${JSON.stringify(
      hPagination
    )}-history-data-${JSON.stringify(statusFilters)}`,
    queryFn: async () => {
      setHTDataLoading(true);
      setHTData([]);
      return await axiosInstance.post(
        `/operationmonitor/api/v1/workflow-history`,
        {
          actionType: selectedActionType,
          onlyUserApproved: showUserApproved,
          sort: getDataGridSortOrderForPayload(
            historicalSortOrder,
            TABLE_TYPES_KEYS[1]
          ),
          searchTerm: historicalSearchTerm,
          startDate: formatDateString(dateRangeFilter[0], true),
          endDate: formatDateString(dateRangeFilter[1], false),
          offset: hPagination.page + 1,
          limit: hPagination.pageSize,
          status: statusFilters,
        }
      );
    },
    ...{
      enabled:
        isFetchEnabled &&
        isSelectedTableTabHistorical(selectedTableTab) &&
        enableWorkflowActionEndpoints(selectedActionType),
      refetchOnWindowFocus: false,
    },
  });

  const { data: hTableCount, refetch: hTableCountRefetch } = useQuery({
    queryKey: `${selectedActionType}-${userEmail}-${historicalSearchTerm}-${showUserApproved}-${JSON.stringify(
      dateRangeFilter
    )}-history-datacount-${JSON.stringify(statusFilters)}`,
    queryFn: async () =>
      await axiosInstance.post(
        `/operationmonitor/api/v1/workflow-history/count`,
        {
          actionType: selectedActionType,
          onlyUserApproved: showUserApproved,
          searchTerm: historicalSearchTerm,
          startDate: formatDateString(dateRangeFilter[0], true),
          endDate: formatDateString(dateRangeFilter[1], false),
          status: statusFilters,
        }
      ),
    ...{
      enabled:
        isFetchEnabled &&
        isSelectedTableTabHistorical(selectedTableTab) &&
        enableWorkflowActionEndpoints(selectedActionType),
      refetchOnWindowFocus: false,
    },
  });

  const {
    data: industryData,
    isFetching: industryDataLoading,
    refetch: industryDataRefetch,
  } = useQuery({
    queryKey: `aging-segment-pie-data-${selectedActionType}`,
    queryFn: async () => {
      setPieDataLoading(true);
      setIndustryTableData([]);
      setIndustryPieData({});
      setIndustrySankeyData([sankeyColumns]);
      return await axiosInstance.get(
        `/operationmonitor/api/v1/workflow-action/piechart/industry`,
        {
          params: {
            actionType: selectedActionType,
          },
        }
      );
    },
    ...{
      enabled:
        isFetchEnabled && enableWorkflowActionEndpoints(selectedActionType),
      refetchOnWindowFocus: false,
    },
  });

  const {
    data: pendingCollectionsFailureDirectIndirectData,
    isFetching: pendingCollectionsFailureDirectIndirectDataLoading,
    refetch: pendingCollectionsFailureDirectIndirectDataRefetch,
  } = useQuery({
    queryKey: `aging-pending-failure-direct-indirect-${selectedActionType}`,
    queryFn: async () => {
      setDaysDataLoading(true);
      setPendingCollectionsFailureDirectIndirect({});
      return await axiosInstance.get(
        `/operationmonitor/api/v1/workflow-action/barchart/day`,
        {
          params: {
            actionType: selectedActionType,
          },
        }
      );
    },
    ...{
      enabled:
        isFetchEnabled && enableWorkflowActionEndpoints(selectedActionType),
      refetchOnWindowFocus: false,
    },
  });

  const {
    data: pastSubsCancellationCountData,
    isFetching: subsCancellationCountDataLoading,
    refetch: subsCancellationCountDataRefetch,
  } = useQuery({
    queryKey: `aging-past-subs-cancellation-count-${selectedActionType}`,
    queryFn: async () => {
      setMonthDataLoading(true);
      setPastSubsCancellationCount({});
      return await axiosInstance.get(
        `/operationmonitor/api/v1/workflow-action/barchart/month`,
        {
          params: {
            actionType: selectedActionType,
          },
        }
      );
    },
    ...{
      enabled:
        isFetchEnabled && enableWorkflowActionEndpoints(selectedActionType),
      refetchOnWindowFocus: false,
    },
  });

   const handleDeclineExecution = async (rowId, extendedDate, comments) => {
   if (!rowId || !extendedDate) {
     enqueueSnackbar("Please pick an Extended Date.", { variant: "error" });
     return;
   }
   if (!comments || !`${comments}`.trim()) {
      enqueueSnackbar("Comments are required for deferring cancellation.", { variant: "error" });
      return;
    }
   await axiosInstance
     .put(
       `/operationmonitor/api/v1/workflow-action/cancellation-extension/${rowId}`,
       { executionDate: extendedDate, comments:comments}
     )
     .then(() =>
       enqueueSnackbar("Cancellation execution date extended. Change may take up to 2 hours to reflect on the cancellation dashboard.", {
         variant: "success",
       })
     )
     .catch(() =>
       enqueueSnackbar("Error extending execution date.", {
         variant: "error",
       })
     )
     .finally(() => {
       refetchFunc();
     });
 };


  const refetchFunc = () => {
    // refresh table
    if (isSelectedTableTabPending(selectedTableTab)) {
      tableDataRefetch();
    } else {
      hTableDataRefetch();
    }
    tableCountRefetch();
    hTableCountRefetch();
    // refresh Pending Cancellation By Country
    geoMapDataRefetch();

    // refresh Top 10 Pending Cancellation By Industry
    industryDataRefetch();

    // refresh Pending Cancellation
    pendingCollectionsFailureDirectIndirectDataRefetch();

    // refresh Past Subscription Cancellation Count
    subsCancellationCountDataRefetch();
  };

  useEffect(() => {
    if (tData?.length) setHasPendingTData(true);
    else setHasPendingTData(false);
  }, [tData]);

  useEffect(() => {
    if (hTData?.length) setHasHistoricalTData(true);
    else setHasHistoricalTData(false);
  }, [hTData]);

  useEffect(() => {
    /* this use effect is required since l2Status.date is not a dedicated column is history table,
    hence onSortModelChange returns empty list */
    if (changeableHistoricalSortOrder.length > 0) {
      setHistoricalSortOrder(changeableHistoricalSortOrder);
    } else if (changeableHistoricalSortOrder.length === 0) {
      setHistoricalSortOrder([DEFAULT_SORT_ORDER.historical]);
    }
  }, [changeableHistoricalSortOrder]);

  useEffect(() => {
    // reset page number, page size and due in sort on action type change
    setPagination({
      page: 0,
      pageSize: 10,
    });
    setHPagination({
      page: 0,
      pageSize: 10,
    });

    setSortOrder([DEFAULT_SORT_ORDER.pending]);
    setHistoricalSortOrder([DEFAULT_SORT_ORDER.historical]);
    handleChangeHistoricalSortOrder([]);

    // table reset for search, show mine, show L1 approved, approver level 1, no rows selected
    setSearchTerm("");
    setHistoricalSearchTerm("");
    setShowAll(false);
    setShowUserApproved(false);
    setHideL1Approved(false);
    setApproverLevel("level_one");
    setSelectedRowIds([]);
    setSelectedRows([]);
    goToTop();

    // reset industry section
    setShowAllIndustryData(false);
  }, [selectedActionType]);

  useEffect(() => {
    if (!showAll) {
      setHideL1Approved(false);
    }
    setApproverLevel("level_one");
  }, [showAll]);

  useEffect(() => {
    if (!geoMapDataLoading) {
      if (geoMapData?.data?.cancellations) {
        const data = geoMapData.data.cancellations || [];
        const res = data.map((item) => [item.country, item.value]);
        res.unshift(["Country", "Cancellations"]);
        setGeoData(res);
      } else {
        setGeoData([["Country", "Cancellations"]]);
      }
    }
  }, [geoMapData, geoMapDataLoading]);

  useEffect(() => {
    setRowCount(tableCount?.data?.data || 0);
  }, [tableCount]);

  useEffect(() => {
    setHRowCount(hTableCount?.data?.data || 0);
  }, [hTableCount]);

  useEffect(() => {
    const data = userActionData?.data?.data;
    if (data) {
      setUserEmail(data.email);
      setActionType(data.actions || []);
    }
  }, [userActionData]);

  useEffect(() => {
    if (
      !userActionDataLoading &&
      actionType &&
      selectedActionType &&
      selectedTableTab
    ) {
      setFetchEnabled(true);
      setStatusFilters(DEFAULT_STATUS_FILTERS);
    }
  }, [actionType, selectedActionType, selectedTableTab, userActionDataLoading]);

  useEffect(() => {
    if (tableData?.data?.data) {
      tableData.data.data?.forEach((item) => {
        item._id = item?.id;
        item.id = item?.id;

        const level1ApprovesNames = item?.levelApproves?.["1"]
          ?.filter((approver) => approver.name && approver.name !== "")
          ?.map((approver) => approver.name) || [""];
        const level2ApprovesNames = item?.levelApproves?.["2"]
          ?.filter((approver) => approver.name && approver.name !== "")
          ?.map((approver) => approver.name) || [""];

        item.level_1_approver = item?.l1Status?.name
          ? [item.l1Status.name]
          : level1ApprovesNames;
        item.level_1_status = item?.l1Status?.status || "";
        item.level_1_statusDate = item?.l1Status?.date || "";

        item.level_2_approver = item?.l2Status?.name
          ? [item.l2Status.name]
          : level2ApprovesNames;
        item.level_2_status = item?.l2Status?.status || "";
        item.level_2_statusDate = item?.l2Status?.date || "";

        if (item?.l2Status?.comments) {
          item.detailedInfo = item.l2Status.comments;
        } else if (item?.l1Status?.comments) {
          item.detailedInfo = item.l1Status.comments;
        } else {
          item.detailedInfo = "";
        }
      });
      setTData(tableData.data.data);
    } else {
      setTData([]);
    }
    setTDataLoading(false);
  }, [selectedActionType, tableData]);

  useEffect(() => {
    if (hTableData?.data?.data) {
      hTableData.data.data?.forEach((item) => {
        item._id = item?.id;
        item.id = item?.id;

        item.level_1_approver = item?.l1Status?.name
          ? [item.l1Status.name]
          : "";
        item.level_1_status = item?.l1Status?.status || "";
        item.level_1_statusDate = item?.l1Status?.date || "";

        item.level_2_approver = item?.l2Status?.name
          ? [item.l2Status.name]
          : "";
        item.level_2_status = item?.l2Status?.status || "";
        item.level_2_statusDate = item?.l2Status?.date || "";

        if (item?.l2Status?.comments) {
          item.detailedInfo = item.l2Status.comments;
        } else if (item?.l1Status?.comments) {
          item.detailedInfo = item.l1Status.comments;
        } else {
          item.detailedInfo = "";
        }
      });
      setHTData(hTableData.data.data);
    } else {
      setHTData([]);
    }
    setHTDataLoading(false);
  }, [selectedActionType, hTableData]);

  useEffect(() => {
    if (!industryDataLoading) {
      const data = [...(industryData?.data?.cancellations || [])];
      if (data.length) {
        // sort industries by count in descending order
        // please lease with ritesh.singh@zoom.us before removing sort login in UI
        data.sort((a, b) => b.count - a.count);
        const filteredData = getTop10AndRemoveUncategorisedIndustry(data) || [];
        if (isSelectedActionTypeTNS(selectedActionType)) {
          // for tns action types we use sankey
          setIndustrySankeyData((old) => {
            const sankeyData = filteredData.map((item) => {
              const styledTooltip = getSankeyIndustryTooltipContent(item);
              return [item.reasonCode, item.id, item.count, styledTooltip];
            });
            old.push(...sankeyData);
            return old;
          });
        } else {
          // for other action types we use pie
          // total count only for the top 10 industries
          // please lease with ritesh.singh@zoom.us before removing totalCount calculation logic in UI
          const totalCount =
            filteredData
              ?.map((item) => item.count)
              ?.reduce(
                (accumulator, currentValue) => accumulator + currentValue,
                0
              ) || 1;
          // add percent only for the top 10 industries
          filteredData.forEach((item) => {
            item.percent = ((item.count / totalCount) * 100).toFixed(2);
          });
          setIndustryPieData({
            theme: "ag-polychroma",
            data: filteredData,
            totalCount: totalCount,
            autoSize: false,
            series: [
              {
                type: "pie",
                angleKey: "count",
                legendItemKey: "id",
                calloutLabelKey: "percent",
                calloutLabel: {
                  fontWeight: "bold",
                  formatter: (item) => `${item.value}%`,
                },
                visible: true,
                crisp: true,
                shadow: {
                  enabled: true,
                },
                cursor: "pointer",
                innerRadiusRatio: 0.5,
                tooltip: {
                  showArrow: true,
                  wrapping: "always",
                  renderer: (
                    params
                  ) => `<div class="ag-chart-tooltip-title" style="background-color: ${params.color
                  }">
          ${params.datum.id}
          </div>
          <div class="ag-chart-tooltip-content">
          ${parseFloat(params.datum.count).toFixed(0)}
          </div>`,
                  interaction: {
                    enabled: true,
                  },
                },
              },
            ],
            overlays: {
              noData: {
                enabled: true,
                text: `No data to display`,
              },
            },
          });
        }
        setIndustryTableData(data);
      } else {
        setIndustryTableData([]);
        setIndustryPieData({});
        setIndustrySankeyData([sankeyColumns]);
      }
      setPieDataLoading(false);
    }
  }, [industryData, industryDataLoading]);

  useEffect(() => {
    if (!subsCancellationCountDataLoading) {
      const data = pastSubsCancellationCountData?.data || {};
      const cancellationsData = data.cancellations || [];
      cancellationsData.forEach((item) => {
        item.month = MONTHS[item.month];
      });
      setPastSubsCancellationCount({
        data: cancellationsData,
        autoSize: false,
        series: [
          {
            type: "bar",
            xKey: "month",
            yKey: "count",
            cursor: "pointer",
            tooltip: {
              showArrow: true,
              wrapping: "always",
              renderer: (params) => {
                return `<div class="ag-chart-tooltip-title" style="background-color: ${params.color
                  }">
                Cancellation Count
              </div>
              <div class="ag-chart-tooltip-content">
                ${parseFloat(params.datum.count).toFixed(0)}
              </div>`;
              },
              interaction: {
                enabled: true,
              },
            },
            stroke: "#ed8a8a",
            fill: "#ed8a8a",
            cornerRadius: 10,
            showInLegend: true,
            visible: true,
            crisp: true,
            shadow: {
              enabled: true,
            },
          },
        ],
        axes: [
          {
            type: "category",
            position: "bottom",
            title: {
              enabled: true,
              text: "Months",
              formatter: (params) => `${params.defaultValue}`,
            },
            label: { autoRotate: false, avoidCollisions: true },
          },
          {
            type: "number",
            position: "left",
            title: {
              enabled: true,
              text: "Count",
              formatter: (params) => `${params.defaultValue}`,
            },
            label: {
              autoRotate: false,
              avoidCollisions: true,
            },
          },
        ],
        overlays: {
          noData: {
            enabled: true,
            text: `No data to display`,
          },
        },
      });
      setMonthDataLoading(false);
    }
  }, [pastSubsCancellationCountData, subsCancellationCountDataLoading]);

  useEffect(() => {
    if (!pendingCollectionsFailureDirectIndirectDataLoading) {
      const data = pendingCollectionsFailureDirectIndirectData?.data || {};
      setPendingCollectionsFailureDirectIndirect({
        data: data.cancellations || [],
        total: data.total || 0,
        totalMRR: getFormattedAmount(data.totalMrr),
        days: data.days || 0,
        boundaryDays: data.boundaryDays || 0,
        autoSize: false,
        series: [
          {
            type: "histogram",
            aggregation: "sum",
            xKey: "day",
            yKey: "count",
            bins: [
              [0, 5],
              [5, 10],
              [10, 15],
              [15, 20],
              [20, 25],
              [25, 30],
            ],
            cursor: "pointer",
            tooltip: {
              renderer: (params) => {
                const aggregateCount =
                  params.datum?.data?.reduce(
                    (sum, entry) => sum + entry.count,
                    0
                  ) || 0;
                const aggregateMRR =
                  params.datum?.data?.reduce(
                    (sum, entry) => sum + entry.mrr,
                    0
                  ) || 0;
                return `<div class="ag-chart-tooltip-container">
          <div>
            <div
              class="ag-chart-tooltip-title"
              style="background-color: ${params.color}"
            >
              MRR
            </div>
            <div class="ag-chart-tooltip-content">
              $${parseFloat(aggregateMRR).toFixed(2)}
            </div>
          </div>
          <div>
            <div
              class="ag-chart-tooltip-title"
              style="background-color: ${params.color}"
            >
              Count
            </div>
            <div class="ag-chart-tooltip-content">
              ${parseFloat(aggregateCount).toFixed(0)}
            </div>
          </div>
        </div>`;
              },
              interaction: {
                enabled: true,
              },
            },
            cornerRadius: 10,
            showInLegend: true,
            visible: true,
            crisp: true,
            shadow: {
              enabled: true,
            },
          },
        ],
        axes: [
          {
            type: "number",
            position: "bottom",
            reverse: true,
            title: {
              enabled: true,
              text: "Due for Cancel (Days)",
              formatter: (params) => `${params.defaultValue}`,
            },
            label: { autoRotate: false, avoidCollisions: true },
          },
          {
            type: "number",
            position: "left",
            title: {
              enabled: true,
              text: "Number of Accounts",
              formatter: (params) => `${params.defaultValue}`,
            },
            label: {
              autoRotate: false,
              avoidCollisions: true,
            },
          },
        ],
        overlays: {
          noData: {
            enabled: true,
            text: `No data to display`,
          },
        },
      });
      setDaysDataLoading(false);
    }
  }, [
    pendingCollectionsFailureDirectIndirectData,
    pendingCollectionsFailureDirectIndirectDataLoading,
  ]);

  useEffect(() => {
    setSelectedRows(
      (old) =>
        unionBy(old, tData, "id")?.filter((row) =>
          selectedRowIds.includes(row.id)
        ) || []
    );
  }, [tData, selectedRowIds]);

  useEffect(() => {
    if (!isApprovalRejectionModalOpen) {
      setSelectedRowIds([]);
      setSelectedRows([]);
    }
  }, [isApprovalRejectionModalOpen]);

  const handleApproveOrReject = async (
    action,
    comments,
    cancellationReason,
    promisedPayDate,
    applyPromisedPayDate
  ) => {
    const isNonPaymentDirect =
      isSelectedActionTypeNonPaymentDirect(selectedActionType);
    const isBizOperationDirect =
      isSelectedActionTypeBizOperationDirect(selectedActionType);

    return await axiosInstance
      .put(`/operationmonitor/api/v1/workflow-action`, {
        id: selectedRows?.map((row) => row._id),
        actionType: selectedActionType,
        action: action,
        comments: comments,
        ...((isNonPaymentDirect || isBizOperationDirect) &&
          action === "Rejected" && {
          cancellationReason: cancellationReason,
          promisedPayDate: promisedPayDate,
          applyPromisedPayDate: applyPromisedToPayFunc(applyPromisedPayDate),
        }),
      })
      .then((success) => {
        const messgae = `${success?.data?.data || 0} record(s) ${action?.toLowerCase() || "modified"
          } successfully`;
        enqueueSnackbar(messgae, { variant: "success" });
      })
      .catch(() => {
        const messgae = `Error ${action === "Rejected" ? "rejecting" : "approving"
          } ${selectedRows.length} record(s)`;
        enqueueSnackbar(messgae, { variant: "error" });
      })
      .finally(() => {
        refetchFunc();
      });
  };

  return (
    <div className="table_container">
      <div
        style={{
          width: "100%",
          overflowX: "auto",
        }}
      >
        <TopBar
          actionTypes={actionType}
          setSelectedActionType={setSelectedActionType}
          setSelectedTableTab={setSelectedTableTab}
          isAdmin={!!(userActionData?.data?.data?.isAdmin || userActionData?.data?.data?.admin)}
        />

        {isSelectedActionTypeAdmin(selectedActionType) ? (
          <AdminPortal />
        ) : (
        <>

        <Box
          sx={{
            display: "grid",
            justifyContent: "left",
            marginLeft: "5px",
          }}
        >
          <Typography className="show-all-text">Show Charts</Typography>
          <ButtonBase onClick={() => setShowCharts((old) => !old)}>
            <Switch size="small" checked={showCharts} />
          </ButtonBase>
        </Box>

        {showCharts ? (
          <>
            <Box className={classNames("responsive-container", "container-1")}>
              <div
                className={classNames("responsive-row-1", "responsive-item")}
              >
                <Box>
                  <Box sx={{ padding: 2 }}>
                    <Box sx={{ textAlign: "left" }}>
                      <Typography
                        variant="h6"
                        gutterBottom
                        className={classNames(
                          "operations-container-title",
                          "operations-3d-title"
                        )}
                      >
                        Pending Cancellation By Country
                      </Typography>
                    </Box>
                    <Divider sx={{ marginBottom: 2 }} />
                  </Box>
                  {!geoMapDataLoading ? (
                    <div className="tile-centralise-content">
                      <Chart
                        chartEvents={[
                          {
                            eventName: "select",
                            callback: ({ chartWrapper }) => {
                              const chart = chartWrapper.getChart();
                              const selection = chart.getSelection();
                              if (selection.length === 0) return;
                            },
                          },
                        ]}
                        chartType="GeoChart"
                        width="100%"
                        height="450px"
                        data={geoData}
                        options={geoChartOptions}
                        className="cancellations-geochart"
                        style={{ maxWidth: "900px" }}
                        legendToggle
                      />
                    </div>
                  ) : (
                    <div className="tile-centralise-content">
                      <CircularProgress size="3rem" />
                    </div>
                  )}
                </Box>
              </div>
              <div
                className={classNames("responsive-row-1", "responsive-item")}
              >
                <Box>
                  <div
                    style={{
                      width: "100%",
                      height: "100%",
                    }}
                  >
                    <Box sx={{ padding: 2 }}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <Box sx={{ textAlign: "left" }}>
                          <Typography
                            variant="h6"
                            gutterBottom
                            className={classNames(
                              "operations-container-title",
                              "operations-3d-title"
                            )}
                          >
                            {`${!showAllIndustryData ? "Top 10 " : ""
                              }Pending Cancellation By Industry`}
                          </Typography>
                        </Box>
                        <Box>
                          <IconButton
                            style={{ alignContent: "end" }}
                            onClick={() => {
                              setShowAllIndustryData((old) => !old);
                            }}
                          >
                            {industryTableData?.length ? (
                              !showAllIndustryData ? (
                                <GridOnRoundedIcon />
                              ) : (
                                <PieChartOutlineRoundedIcon />
                              )
                            ) : (
                              <></>
                            )}
                          </IconButton>
                        </Box>
                      </Box>
                      <Divider sx={{ marginBottom: 2 }} />
                    </Box>
                    <IndustryDataSection
                      industryPieData={industryPieData}
                      industrySankeyData={industrySankeyData}
                      showAllIndustryData={showAllIndustryData}
                      industryTableData={industryTableData}
                      selectedActionType={selectedActionType}
                      pieDataLoading={pieDataLoading}
                      industryDataLoading={industryDataLoading}
                      userActionDataLoading={userActionDataLoading}
                    />
                  </div>
                </Box>
              </div>
            </Box>
            <Box className={classNames("responsive-container", "container-2")}>
              <div
                className={classNames("responsive-row-2", "responsive-item")}
              >
                <Box
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    minHeight: "100%",
                  }}
                >
                  <Box>
                    <Box sx={{ padding: 2 }}>
                      <Box sx={{ textAlign: "left" }}>
                        <Typography
                          variant="h6"
                          gutterBottom
                          className={classNames(
                            "operations-container-title",
                            "operations-3d-title"
                          )}
                        >
                          {selectedActionType
                            ? `Pending ${SIDEBAR_ITEMS[selectedActionType]} Cancellations`
                            : `Pending Cancellations`}
                        </Typography>
                      </Box>
                      <Divider sx={{ marginBottom: 2 }} />
                    </Box>
                    {!userActionDataLoading &&
                      !daysDataLoading &&
                      !pendingCollectionsFailureDirectIndirectDataLoading ? (
                      <PendingCancellationsHeader
                        total={pendingCollectionsFailureDirectIndirect.total}
                        totalMRR={
                          pendingCollectionsFailureDirectIndirect.totalMRR
                        }
                        days={pendingCollectionsFailureDirectIndirect.days}
                        boundaryDays={
                          pendingCollectionsFailureDirectIndirect.boundaryDays
                        }
                      />
                    ) : (
                      <></>
                    )}
                    {!userActionDataLoading &&
                      !daysDataLoading &&
                      !pendingCollectionsFailureDirectIndirectDataLoading ? (
                      <AgCharts
                        options={pendingCollectionsFailureDirectIndirect}
                        className="pending_cancellations_day"
                      />
                    ) : (
                      <div className="tile-centralise-content">
                        <CircularProgress size="3rem" />
                      </div>
                    )}
                  </Box>
                </Box>
              </div>
              <div
                className={classNames("responsive-row-2", "responsive-item")}
              >
                <Box
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    minHeight: "100%",
                  }}
                >
                  <Box sx={{ padding: 2 }}>
                    <Box sx={{ textAlign: "left" }}>
                      <Typography
                        variant="h6"
                        gutterBottom
                        className={classNames(
                          "operations-container-title",
                          "operations-3d-title"
                        )}
                      >
                        Past Subscription Cancellation Count
                      </Typography>
                    </Box>
                    <Divider sx={{ marginBottom: 2 }} />
                  </Box>
                  {!userActionDataLoading &&
                    !monthDataLoading &&
                    !subsCancellationCountDataLoading ? (
                    <AgCharts
                      options={pastSubsCancellationCount}
                      style={{ marginTop: "auto" }}
                    />
                  ) : (
                    <div className="tile-centralise-content">
                      <CircularProgress size="3rem" />
                    </div>
                  )}
                </Box>
              </div>
            </Box>
          </>
        ) : (
          <></>
        )}
        <div className="searchbar-wrapper">
          <CancellationTableTab
            selectedTableTab={selectedTableTab}
            setSelectedTableTab={setSelectedTableTab}
          />

          {isSelectedTableTabHistorical(selectedTableTab) ? (
            <HistoricalSearchBar
              selectedActionType={selectedActionType}
              historicalSearchTerm={historicalSearchTerm}
              setHistoricalSearchTerm={setHistoricalSearchTerm}
              showUserApproved={showUserApproved}
              setShowUserApproved={setShowUserApproved}
              dateRangeFilter={dateRangeFilter}
              setDateRangeFilter={setDateRangeFilter}
              sortOrder={sortOrder}
              pagination={pagination}
              hasHistoricalTData={hasHistoricalTData}
              statusFilters={statusFilters}
              setStatusFilters={setStatusFilters}
            />
          ) : (
            <PendingSearchBar
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              setApproveAction={setApproveAction}
              isExactlyOneSelected={selectedRowIds?.length === 1}
              setExecutionDecline={setExecutionDecline}
              setApprovalRejectionModalOpen={setApprovalRejectionModalOpen}
              isRecordSelected={selectedRowIds?.length > 0}
              isApproveEligible={isApproveEligible}
              showAll={showAll}
              setShowAll={setShowAll}
              hideL1Approved={hideL1Approved}
              setHideL1Approved={setHideL1Approved}
              approverLevel={approverLevel}
              setApproverLevel={setApproverLevel}
              selectedActionType={selectedActionType}
              sortOrder={sortOrder}
              pagination={pagination}
              hasPendingTData={hasPendingTData}
            />
          )}
          {!tDataLoading &&
            !isTableDataFetching &&
            !hTDataLoading &&
            !isHTableDataFetching ? (
            <div style={{ width: "100%" }}>
              <div className="datagrid-parent-container">
                {isSelectedTableTabPending(selectedTableTab) ? (
                  <DataGrid
                    rows={tData}
                    columns={columnsDashboard(selectedActionType)}
                    columnHeaderHeight={95}
                    rowHeight={65}
                    initialState={gridColumnState}
                    pageSizeOptions={[10, 20, 50]}
                    pagination
                    paginationPageSize={10}
                    cacheBlockSize={10}
                    paginationMode="server"
                    sortingMode="server"
                    filterMode="server"
                    rowsLoadingMode="server"
                    sortModel={sortOrder}
                    onSortModelChange={setSortOrder}
                    paginationModel={pagination}
                    onPaginationModelChange={setPagination}
                    checkboxSelection={
                      !showAll && !isCurrentRoleReadonly(currentRoles)
                    }
                    isRowSelectable={(params) =>
                      !NON_ACTIONABLE_PROCESSING_STATUSES.includes(
                        params?.row?.processingStatus
                      ) &&
                      // A user's roles are now global (e.g. "Trust and Safety Senior
                      // Manager" + "Readonly"), so isCurrentRoleReadonly alone can't tell
                      // whether this user can act on THIS row's tab/actionType - a role
                      // valid only on another tab would otherwise light up checkboxes
                      // everywhere. levelApproves is per-record/per-level truth: only make
                      // a row selectable if the current user is actually listed as an
                      // approver for its currently-pending level.
                      //
                      // A pure Renewals Defer Approver is never in levelApproves (defer
                      // visibility is an additive grant independent of the normal
                      // approver mechanism), so that check alone would wrongly grey out
                      // every row for them - OR in the separate defer-eligibility check.
                      (isUserEligibleToApproveRow(params?.row, currentUserEmail) ||
                        isUserEligibleToDeferRow(
                          params?.row,
                          isRenewalsDeferApprover,
                          selectedActionType
                        ))
                    }
                    disableRowSelectionOnClick={true}
                    rowSelectionModel={selectedRowIds}
                    onRowSelectionModelChange={setSelectedRowIds}
                    keepNonExistentRowsSelected={true}
                    sx={{
                      "& .MuiDataGrid-columnHeaders": {
                        fontWeight: "bold",
                      },
                      height: "855px",
                    }}
                    slots={{
                      toolbar: CustomDataGridToolBar,
                      noRowsOverlay: () => (
                        <NoRecords refetchFunc={refetchFunc}>
                          {showAll ? (
                            <p>No approval(s) pending.</p>
                          ) : (
                            <p>
                              No approval(s) pending for you. Please click on{" "}
                              <strong>Show All</strong> to view more data.
                            </p>
                          )}
                        </NoRecords>
                      ),
                    }}
                    rowCount={rowCount}
                    autoHeight={false}
                    getCellClassName={(params) =>
                      `operations-datagrid-cell operations-datagrid-col-${params?.field}-${params?.id}`
                    }
                    getRowClassName={(params) =>
                      `operations-datagrid-row-${params?.id}`
                    }
                    onClipboardCopy={setCopiedData}
                    slotProps={{
                      columnsManagement: {
                        getTogglableColumns: (params) =>
                          Object.keys(
                            gridColumnState?.columns?.columnVisibilityModel ||
                            {}
                          )?.filter(
                            (item) =>
                              !["__check__", "accountNumber", "name"].includes(
                                item
                              )
                          ) || [],
                      },
                    }}
                  />
                ) : (
                  <DataGrid
                    rows={hTData}
                    columns={hColumnsDashboard(selectedActionType)}
                    columnHeaderHeight={90}
                    rowHeight={65}
                    initialState={gridColumnState}
                    pageSizeOptions={[10, 20, 50]}
                    pagination
                    paginationPageSize={10}
                    cacheBlockSize={10}
                    paginationMode="server"
                    sortingMode="server"
                    filterMode="server"
                    rowsLoadingMode="server"
                    sortModel={changeableHistoricalSortOrder}
                    onSortModelChange={handleChangeHistoricalSortOrder}
                    paginationModel={hPagination}
                    onPaginationModelChange={setHPagination}
                    checkboxSelection={false}
                    disableRowSelectionOnClick={true}
                    keepNonExistentRowsSelected={true}
                    sx={{
                      "& .MuiDataGrid-columnHeaders": {
                        fontWeight: "bold",
                      },
                      height: "850px",
                    }}
                    slots={{
                      toolbar: CustomDataGridToolBar,
                      noRowsOverlay: () => (
                        <NoRecords refetchFunc={refetchFunc}>
                          {showAll ? (
                            <p>No approval(s) pending.</p>
                          ) : (
                            <p>
                              No approval(s) pending for you. Please click on{" "}
                              <strong>Show All</strong> to view more data.
                            </p>
                          )}
                        </NoRecords>
                      ),
                      loadingOverlay: () => (
                        <Box
                          sx={{
                            height: "max-content",
                          }}
                        >
                          {[...Array(10)].map((_, key) => (
                            <Skeleton
                              animation="wave"
                              variant="rectangular"
                              sx={{ my: 4, mx: 1 }}
                              key={key}
                            />
                          ))}
                        </Box>
                      ),
                    }}
                    rowCount={hRowCount}
                    autoHeight={false}
                    getCellClassName={(params) =>
                      `operations-datagrid-cell operations-datagrid-col-${params?.field}-${params?.id}`
                    }
                    getRowClassName={(params) =>
                      `operations-datagrid-row-${params?.id}`
                    }
                    onClipboardCopy={setCopiedData}
                    slotProps={{
                      columnsManagement: {
                        getTogglableColumns: (params) =>
                          Object.keys(
                            gridColumnState?.columns?.columnVisibilityModel ||
                            {}
                          )?.filter(
                            (item) =>
                              !["__check__", "accountNumber", "name"].includes(
                                item
                              )
                          ) || [],
                      },
                    }}
                  />
                )}
              </div>
              <CopiedData data={copiedData} />
            </div>
          ) : (
            <div className="loader-container" style={{ height: "70.5vh" }}>
              <CircularProgress size="3rem" />
            </div>
          )}
        </div>
        </>
        )}
      </div>
      <ApprovalRejectionModal
        isOpen={isApprovalRejectionModalOpen}
        onClose={() => {
          setApprovalRejectionModalOpen(false);
          setExecutionDecline(false);
        }}
        selectedActionType={selectedActionType}
        isApproveAction={isApproveAction}
        isExecutionDecline={isExecutionDecline}
        approverLevel={approverLevel}
        selectedRows={selectedRows}
        setSelectedRowIds={setSelectedRowIds}
        handleApproveOrReject={handleApproveOrReject}
        handleDeclineExecution={handleDeclineExecution}
      />
    </div>
  );
};

const getSankeyIndustryTooltipContent = (item) =>
  `<div style="display: grid; padding: 10px; text-align: left;">
    <span style="white-space: nowrap;">
      <strong>Industry</strong>: ${item.id}
    </span>
    <span style="white-space: nowrap;">
      <strong>Cancellation Reason</strong>: ${item.reasonCode}
    </span>
    <span style="white-space: nowrap;">
      <strong>Count</strong>: ${item.count}
    </span>
  </div>`;

export default Home;
